<template>
  <div class="product-grid containerV2" :class="classes">
    <template v-for="product in products" :key="product.id">
      <ProductCardHelper
        v-if="!product.skeleton"
        v-slot="{ primaryMedia, secondaryMedia }"
        :product="product"
        :collection-handle="collection?.handle"
        image-type="ecomImages"
      >
        <ProductCard
          :product="product"
          class="product-card"
          :media="primaryMedia"
          :hover-media="secondaryMedia"
          @productClick="$emit('productClick', $event)"
        />
      </ProductCardHelper>
      <SkeletonProductCard v-else />
    </template>
    <FeaturedProductCard
      v-for="(featuredSlot, index) in transformedFeaturedSlots"
      :key="featuredSlot.id"
      :class="`featured-product-card--${index + 1}`"
      :media="featuredSlot.media"
      :product="featuredSlot.product"
    />
  </div>
</template>

<script>
import ProductCard from "@/components/ProductCard.vue";
import ProductCardHelper from "@/components/product-card/ProductCardHelper.vue";
import SkeletonProductCard from "@/components/skeleton/SkeletonProductCard.vue";
import FeaturedProductCard from "@/components/collection/FeaturedProductCard.vue";

import { mapState } from "vuex";
import promotionBannerContent from "@/assets/json/collection/PromotionBanner.json";

export default {
  name: "ProductGrid",
  components: {
    ProductCard,
    ProductCardHelper,
    SkeletonProductCard,
    FeaturedProductCard,
  },
  props: {
    narrow: {
      type: Boolean,
      default: false,
    },
    products: {
      type: Array,
      default: () => [],
    },
    featuredSlots: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["productClick"],
  data() {
    return {
      promotionBannerContent,
    };
  },
  computed: {
    ...mapState("collection", ["collection", "loading"]),
    classes() {
      return {
        "product-grid--narrow": this.narrow,
      };
    },
    promotionBanner() {
      return this.promotionBannerContent[this.collection?.handle];
    },
    /**
     * Transform the raw featured metaobjects data into the format expected by FeaturedProductCard
     */
    transformedFeaturedSlots() {
      if (!this.featuredSlots || !Array.isArray(this.featuredSlots)) {
        return [];
      }

      return this.featuredSlots
        .map((metaobject) => {
          console.log(metaobject);

          // Find the media and product fields from the metaobject fields array
          const mediaField = metaobject.fields.find(
            (field) => field.key === "media"
          );
          const productField = metaobject.fields.find(
            (field) => field.key === "product"
          );

          return {
            id: metaobject.id,
            media:
              mediaField?.reference?.image || mediaField?.reference || null,
            product: productField?.reference || null,
          };
        })
        .filter((item) => item.media && item.product); // Filter out any incomplete items
    },
  },
};
</script>

<style lang="scss" scoped>
.product-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 10px;
  row-gap: 40px;

  @include respond-above(md) {
    column-gap: 20px;
  }

  @include respond-above(lg) {
    grid-template-columns: repeat(4, 1fr);
  }

  &.product-grid--narrow {
    grid-template-columns: repeat(2, 1fr);
    column-gap: 10px;
    row-gap: 40px;
    padding: 0;
  }
}

//
//  FEATURED PRODUCT CARD GRID POSITIONING
//––––––––––––––––––––––––––––––––––––––––––––––––––

// Default (Mobile): only odd IDs show, full width at rows 4, 8, 12, etc.
@for $i from 1 through 20 {
  @if $i % 2 == 1 {
    .featured-product-card--#{$i} {
      grid-column: 1 / 3;
      grid-row: #{($i + 1) / 2 * 4}; // Odd IDs: 1→4, 3→8, 5→12, etc.
    }
  } @else {
    .featured-product-card--#{$i} {
      display: none; // Hide even IDs on mobile
    }
  }
}

// Desktop: pairs side by side at rows 3, 6, 9, etc.
@include respond-above(lg) {
  @for $i from 1 through 20 {
    $pair-number: floor(($i + 1) / 2);
    $row: $pair-number * 3;

    .featured-product-card--#{$i} {
      display: block; // Show all IDs on desktop

      @if $i % 2 == 1 {
        grid-column: 1 / 3; // Odd IDs on left
      } @else {
        grid-column: 3 / 5; // Even IDs on right
      }

      grid-row: #{$row};
    }
  }
}
</style>
